/*
    Add-to-cart label

    Several code paths rewrite this button and they do not run in a fixed order, so the
    last writer used to win with whatever variant IT had resolved - leaving "Add To Cart"
    on a backordered variant and, on a thickness change, "BACKORDER NOW" on a sold-out
    one. Deriving the label from the id the form actually holds makes the ordering moot.

    The id sets come from product-block-buy-buttons.liquid: inventory_policy and
    inventory_quantity are not part of Shopify's variant JSON, so this cannot be worked
    out client-side.
*/
const syncAtcLabel = (btn) => {
    if (!btn) return

    const label = btn.querySelector('.btn-atc-text')
    const form = btn.closest('form')
    if (!label || !form) return

    const idField = form.querySelector('[name="id"]')
    const id = idField ? idField.value : ''
    if (!id) return

    const idsFor = (key) => (btn.dataset[key] || '').split(',').filter(Boolean)

    let want
    if (idsFor('soldoutVariantIds').includes(id)) {
        want = window.theme.product.soldOut
    } else if (idsFor('backorderVariantIds').includes(id)) {
        want = btn.dataset.textBackorder || 'BACKORDER NOW'
    } else {
        want = window.theme.product.addToCart
    }

    // Only write on a real difference, so the observer below cannot loop.
    if (label.textContent.trim() !== want) label.textContent = want

    // At least one other writer rewrites this label asynchronously, after the variant
    // has already changed, and it uses its own idea of the variant rather than the id
    // on the form. Watch the label and re-assert, so whoever writes last cannot leave
    // a stale value (a sold-out variant reading BACKORDER NOW, say).
    if (!label.dataset.atcLabelWatched) {
        label.dataset.atcLabelWatched = '1'
        new MutationObserver(() => syncAtcLabel(btn)).observe(label, {
            childList: true,
            characterData: true,
            subtree: true
        })
    }
}
window.syncAtcLabel = syncAtcLabel;

;
/*
    © 2023 EcomGraduates.com
    https://www.ecomgraduates.com
*/

// PATCH: safety fallback — product JSON may come from window.__productJSON
// if the variant-selector block is hidden on this template
(function () {
  if (typeof product === 'undefined' && window.__productJSON) {
    window.product = window.__productJSON;
  }
})();

/*
    Main 'Add to cart' (ATC) form
*/
window.onSubmitAtcForm = async (form, event) => {
    event.preventDefault()

    const btn = form.querySelector('.btn-atc')

    btn.innerHTML = `
        <div class="spinner-border spinner-border-sm" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
    `

    form.classList.add('loading')

    const response = await fetch('/cart/add.js', { method: 'POST', body: new FormData(form) })

    form.classList.remove('loading')
    btn.innerHTML = '<span class="btn-atc-text">Add To Cart</span>';

    window.refreshCartContents(response)

    if (form.hasAttribute('data-ecomify-upsell-modal')) return
    if (btn.closest('.modal')) return

    if (document.querySelector('#offcanvas-cart')) {
        bootstrap.Offcanvas.getOrCreateInstance('#offcanvas-cart').show()
    }
}

/*
    'Add to cart' form based on variant
*/
window.onClickAtcFormVariant = async (btn, event) => {
    const form = btn.closest('form')
    form.querySelector('[name="id"]').value = btn.dataset.variantId

    window.onSubmitAtcForm(form, event)
}

/*
    Product options selector - Listen for change events
*/
// --- VARIANT SELECTOR PREFETCH CACHE ---
// On page load, background-fetch the variant selector HTML for every thickness
// using the Section Rendering API. On thickness click, swap from cache instantly.
window._variantSelectorCache = {};
window._variantSelectorSectionId = null;

const _initVariantSelectorPrefetch = () => {
    const sectionEl = document.querySelector('[id^="shopify-section-"][id*="template-product"]');
    if (!sectionEl) return;

    window._variantSelectorSectionId = String(sectionEl.id).replace('shopify-section-', '');

    const allVariants = window.productVariants
        || (typeof product !== 'undefined' && product.variants)
        || [];

    // First available (price > 0) variant ID per thickness
    const thicknessVariants = {};
    allVariants.forEach(v => {
        const t = v.options[0];
        if (!thicknessVariants[t] && Number(v.price) > 0) {
            thicknessVariants[t] = v.id;
        }
    });

    const handle = (document.querySelector('.single-product') || {}).getAttribute
        ? document.querySelector('.single-product').getAttribute('data-handle')
        : window.location.pathname.split('/products/')[1]?.split('?')[0]?.split('/')[0];

    if (!handle) return;

    Object.keys(thicknessVariants).forEach(thickness => {
        const variantId = thicknessVariants[thickness];
        const doFetch = () => {
            fetch('/products/' + handle + '?variant=' + variantId + '&sections=' + encodeURIComponent(window._variantSelectorSectionId))
                .then(function(r) { return r.json(); })
                .then(function(json) {
                    const html = json[window._variantSelectorSectionId];
                    if (!html) return;
                    const doc = new DOMParser().parseFromString(html, 'text/html');
                    const el = doc.querySelector('.product-variant-selector');
                    if (el) window._variantSelectorCache[thickness] = el;
                })
                .catch(function() {}); // silent — cache miss falls back to fetch
        };
        // Use idle time so prefetch doesn't compete with page rendering
        if (window.requestIdleCallback) {
            requestIdleCallback(doFetch, { timeout: 4000 });
        } else {
            setTimeout(doFetch, 800);
        }
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _initVariantSelectorPrefetch);
} else {
    setTimeout(_initVariantSelectorPrefetch, 600);
}

window.onChangeProductOption = async (input) => {
  const form = input.closest('form');
  if (!form) return;

  // FIX: window.productVariants is never set — fall back to the global
  // `product` object rendered by Liquid: var product = {{ product | json }}
  // Also falls back to window.__productJSON if variant-selector block is hidden.
  const allVariants = window.productVariants
    || (typeof product !== 'undefined' && product.variants)
    || (window.__productJSON && window.__productJSON.variants)
    || [];

  const getCheckedValueByNameContains = (needle) => {
    const radios = [...form.querySelectorAll('.product-option[type="radio"]')]
      .filter(r => (r.getAttribute('name') || '').toLowerCase().includes(needle));
    const checked = radios.find(r => r.checked);
    return checked ? checked.value : null;
  };


  // --- RESOLVE SELECTED VARIANT ---
  const inputName = (input.getAttribute('name') || '').toLowerCase();
  const isThicknessChange = inputName.includes('thickness');
  const thicknessVal = isThicknessChange ? input.value : getCheckedValueByNameContains('thickness');

  let selectedVariant = null;

  if (isThicknessChange) {
    // Bypass DOM entirely for thickness changes — the DOM still shows the previous
    // thickness's packs so radio-based lookups return wrong results.
    // Hidden variants have price:0, so price > 0 correctly skips them.
    selectedVariant =
      allVariants.find(v => v.options[0] === thicknessVal && Number(v.price) > 0 && v.available) ||
      allVariants.find(v => v.options[0] === thicknessVal && Number(v.price) > 0);

    // Update the quantity title span immediately so it shows correct value
    // before the DOM replacement fetch completes
    if (selectedVariant) {
      const qtyWrapper = [...form.querySelectorAll('.product-option-wrapper')]
        .find(w => w.querySelector('input[name="option-quantity"]'));
      const titleSpan = qtyWrapper && qtyWrapper.querySelector('.size-buttons-title span');
      if (titleSpan) titleSpan.textContent = selectedVariant.options[1];
    }
  } else {
    // For quantity/other changes: read checked state from DOM as normal
    const selectedOptions = [];
    form.querySelectorAll('.product-option').forEach(elem => {
      if (elem.type === 'radio') {
        if (elem.checked) selectedOptions.push(elem.value);
      } else {
        selectedOptions.push(elem.value);
      }
    });

    selectedVariant =
      allVariants.find(v =>
        JSON.stringify(v.options) === JSON.stringify(selectedOptions) && String(v.price) !== '0'
      ) ||
      allVariants.find(v =>
        JSON.stringify(v.options) === JSON.stringify(selectedOptions)
      );
  }

  const btn = form.querySelector('.btn-atc');

  if (selectedVariant) {
    form.querySelector('[name="id"]').value = selectedVariant.id;

    if (selectedVariant.available) {
      btn.disabled = false;
      syncAtcLabel(btn);
    } else {
      btn.disabled = true;
      syncAtcLabel(btn);
    }

    const productContent = input.closest('.product-content') || form.closest('.product-content');

    // ── PRICE BLOCK UPDATE ──────────────────────────────────────────────────
    // Prefer real compare_at_price (actual Shopify sale); fall back to synthetic
    // (baseline per-stick × pack count = implicit bulk discount).
    const _getPackCount = (v) => { const m = (v.title || '').match(/(\d+)\s*Pack/i); return m ? parseInt(m[1]) : 0; };
    const _packCount = _getPackCount(selectedVariant);
    const _thickness = selectedVariant.options[0];
    let _basePerStick = _packCount ? selectedVariant.price / _packCount : 0;
    (allVariants || []).forEach(v => {
      if (v.options[0] !== _thickness || Number(v.price) <= 0) return;
      const c = _getPackCount(v); if (!c) return;
      const ps = v.price / c;
      if (ps > _basePerStick) _basePerStick = ps;
    });
    const _syntheticCompare = _packCount ? Math.round(_basePerStick * _packCount) : 0;
    const _isRealSale = selectedVariant.compare_at_price > selectedVariant.price;
    const _effectiveCompare = _isRealSale
      ? selectedVariant.compare_at_price
      : (_syntheticCompare > selectedVariant.price ? _syntheticCompare : 0);

    // Save %: use real compare when available so it's accurate (e.g. $125→$102 = 18%)
    const _savePct = _effectiveCompare
      ? Math.round((1 - selectedVariant.price / _effectiveCompare) * 100)
      : 0;

    const _priceEl = productContent.querySelector('.product-price');

    // Toggle real-sale and has-compare classes on the .product-price container
    _priceEl.classList.toggle('real-sale', _isRealSale);
    _priceEl.classList.toggle('has-compare', !!_effectiveCompare);

    if (_effectiveCompare) {
      // DOM ORDER: final price FIRST (left), strikethrough SECOND (right)
      _priceEl.innerHTML =
        `<div class="product-price-row">` +
        `<span class="product-price-final">` +
        `<span class="visually-hidden">${window.theme.product.priceSale}&nbsp;</span>` +
        `${Shopify.formatMoney(selectedVariant.price)}` +
        `</span>` +
        `<span class="product-price-compare text-muted">` +
        `<span class="visually-hidden">${window.theme.product.priceFrom}&nbsp;</span>` +
        `<s>${Shopify.formatMoney(_effectiveCompare)}</s>` +
        `</span>` +
        `<span class="product-price-per-unit"></span>` +
        `</div>`;
    } else {
      _priceEl.innerHTML =
        `<div class="product-price-row">` +
        `<span class="product-price-final">${Shopify.formatMoney(selectedVariant.price)}</span>` +
        `<span class="product-price-per-unit"></span>` +
        `</div>`;
    }

    // Re-apply classes after innerHTML wipe
    _priceEl.classList.toggle('real-sale', _isRealSale);
    _priceEl.classList.toggle('has-compare', !!_effectiveCompare);

    const _priceRow = _priceEl.querySelector('.product-price-row');
    if (selectedVariant.available && _effectiveCompare && _savePct > 0) {
      _priceRow.insertAdjacentHTML('beforeend',
        `<span class="price-badge-sale badge">${window.theme.product.save}: ${_savePct}%</span>`
      );
    } else if (!selectedVariant.available) {
      _priceRow.insertAdjacentHTML('beforeend',
        `<span class="price-badge-sold-out badge">${window.theme.product.soldOut}</span>`
      );
    }

    // Update per-unit (per-stick) price — element is now guaranteed to exist above
    _updatePerUnitPrice(productContent, selectedVariant, allVariants);

    const purchaseOptionsWrapper = document.querySelector('#product-purchase-options');
    if (purchaseOptionsWrapper) {
      const oneTime = purchaseOptionsWrapper.querySelector('input#purchase-options-one-time + label [data-price]');
      if (oneTime) oneTime.textContent = Shopify.formatMoney(selectedVariant.price);

      const sub = purchaseOptionsWrapper.querySelector('input#purchase-options-subscription + label [data-price]');
      const subPrice = selectedVariant.selling_plan_allocations?.[0]?.price;
      if (sub && subPrice != null) sub.textContent = Shopify.formatMoney(subPrice);
    }

    const skuWrapper = productContent.querySelector('.current-variant-sku');
    if (skuWrapper) {
      if (selectedVariant.sku && selectedVariant.sku.length) {
        skuWrapper.textContent = selectedVariant.sku;
        skuWrapper.removeAttribute('hidden');
      } else {
        skuWrapper.setAttribute('hidden', 'hidden');
      }
    }

    if (input.closest('#product-template')) {
      const url = new URL(window.location);
      url.searchParams.set('variant', selectedVariant.id);
      window.history.replaceState({}, '', url);
    }

    if (btn.querySelector('.btn-atc-price')) {
      btn.querySelector('.btn-atc-price').textContent = Shopify.formatMoney(selectedVariant.price);
    }

    window.dispatchEvent(new CustomEvent('variantChange.ecomify.product', { detail: Object.assign({}, selectedVariant, { _isThicknessChange: isThicknessChange }) }));
  } else {
    btn.disabled = true;
    btn.querySelector('.btn-atc-text').textContent = window.theme.product.unavailable;
  }

  if (input.closest('.color-swatches')) {
    input.closest('.product-option-wrapper').querySelector('.color-swatches-title span').textContent = input.value;
  }

  if (input.closest('.size-buttons')) {
    input.closest('.product-option-wrapper').querySelector('.size-buttons-title span').textContent = input.value;
  }
};


/*
    Update various elements on variant change that need a new document fetch
*/
let variantChangeFetchController = null;

window.addEventListener('variantChange.ecomify.product', async (event) => {
    // Cancel any in-flight fetch from a previous selection
    if (variantChangeFetchController) {
        variantChangeFetchController.abort();
    }

    // --- CACHE HIT: swap variant selector instantly, skip full fetch ---
    // Only on thickness changes — quantity changes don't need a DOM swap.
    const _thickness = event.detail && event.detail.options && event.detail.options[0];
    if (event.detail._isThicknessChange && _thickness && window._variantSelectorCache[_thickness]) {
        const current = document.querySelector('.product-variant-selector');
        if (current) {
            current.replaceWith(window._variantSelectorCache[_thickness].cloneNode(true));
            initInventoryBar();
            initQtyBreaks();
            return; // no fetch needed
        }
    }

    // --- QUANTITY CHANGE: no DOM replacement needed, bail out ---
    if (!event.detail._isThicknessChange) return;

    // --- CACHE MISS: fall back to section rendering API fetch ---
    variantChangeFetchController = new AbortController();
    const signal = variantChangeFetchController.signal;

    const inventoryBar = document.querySelector('#inventory-bar')

    if (inventoryBar) {
        inventoryBar.style.opacity = '.25'
    }
    // Use Section Rendering API (107kb vs 585kb full page)
    let newDocument;
    try {
        const _handle = document.querySelector('.single-product')
            ? document.querySelector('.single-product').getAttribute('data-handle')
            : window.location.pathname.split('/products/')[1]?.split('?')[0]?.split('/')[0];

        if (_handle && window._variantSelectorSectionId) {
            const _sectionUrl = window.location.origin + '/products/' + _handle
                + '?variant=' + event.detail.id
                + '&sections=' + encodeURIComponent(window._variantSelectorSectionId);
            const _res = await fetch(_sectionUrl, { signal });
            const _json = await _res.json();
            const _html = _json[window._variantSelectorSectionId] || '';
            newDocument = new DOMParser().parseFromString(_html, 'text/html');
        } else {
            // Fallback: full page fetch
            const _url = document.querySelector('.single-product')
                ? window.location.origin + '/products/' + document.querySelector('.single-product').getAttribute('data-handle') + '?variant=' + event.detail.id
                : window.location.href;
            const _res = await fetch(_url, { signal });
            const _text = await _res.text();
            newDocument = new DOMParser().parseFromString(_text, 'text/html');
        }
    } catch (e) {
        if (e.name === 'AbortError') return; // newer selection took over
        throw e;
    }

    document.querySelector('#inventory-bar')
        ?.replaceWith(newDocument.querySelector('#inventory-bar'))
    document.querySelector('#product-qty-breaks')
        ?.replaceWith(newDocument.querySelector('#product-qty-breaks'))

    document.querySelector('#product-store-availability-wrapper')
        ?.replaceWith(newDocument.querySelector('#product-store-availability-wrapper'))

    // Only replace the variant selector on thickness changes.
    // Quantity changes don't alter the pack list — replacing the DOM here
    // would reset the user's quantity selection back to the first pack.
    if (event.detail._isThicknessChange) {
        if (document.querySelector('.page-type-product') || document.querySelector('.single-product')) {
            document.querySelector('.product-variant-selector')
                ?.replaceWith(newDocument.querySelector('.product-variant-selector'))
        }
    }

    initInventoryBar()
    initQtyBreaks()

    if (document.querySelector('.product-gallery-variant-based')) {
        document.querySelector('.product-gallery-variant-based')
            ?.replaceWith(newDocument.querySelector('.product-gallery-variant-based'))
        initProductGallery()
    }

    if (document.querySelector('#product-block-variant-description')) {
        document.querySelector('#product-block-variant-description')
            ?.replaceWith(newDocument.querySelector('#product-block-variant-description'))
    }

    document.querySelectorAll('[role="tooltip"]')
        .forEach(el => el.remove())

    document.querySelectorAll('.product-options [data-bs-toggle="tooltip"]')
        .forEach((el) => new bootstrap.Tooltip(el))

})

/*
    Purchase options (selling/subscription plans)
*/
const initPurchaseOptions = () => {
    const wrapper = document.querySelector('#product-purchase-options')

    if (!wrapper) return

    wrapper.querySelectorAll('[name="purchase_option"]').forEach(input => {
        input.addEventListener('change', () => {
            if (input.id === 'purchase-options-one-time') {
                wrapper.querySelector('#purchase-delivery-options').setAttribute('hidden', 'hidden')
                wrapper.querySelector('[name="selling_plan"]').disabled = true

                if (input.closest('#product-template')) {
                    const url = new URL(window.location)
                    url.searchParams.delete('selling_plan')
                    window.history.replaceState({}, '', url)
                }
                const btnAtcPrice = input.closest('form').querySelector('.btn-atc-price')

                if (btnAtcPrice) {
                    // The money-formatted value lives on the <b data-price> inside this
                    // option's label. The radio has no data-price, so the old
                    // Shopify.formatMoney(undefined) rendered $0.00 on every toggle.
                    const optPriceEl = (input.closest('.form-check') || input.parentElement).querySelector('[data-price]')
                    if (optPriceEl) btnAtcPrice.textContent = optPriceEl.textContent.trim()
                }
            } else {
                wrapper.querySelector('#purchase-delivery-options').removeAttribute('hidden')
                wrapper.querySelector('[name="selling_plan"]').disabled = false

                if (input.closest('#product-template')) {
                    const url = new URL(window.location)
                    url.searchParams.set('variant', wrapper.closest('.product-content').querySelector('[name="id"]').value)
                    url.searchParams.set('selling_plan', wrapper.querySelector('[name="selling_plan"]').value)
                    window.history.replaceState({}, '', url)
                }

                const btnAtcPrice = input.closest('form').querySelector('.btn-atc-price')

                if (btnAtcPrice) {
                    // The money-formatted value lives on the <b data-price> inside this
                    // option's label. The radio has no data-price, so the old
                    // Shopify.formatMoney(undefined) rendered $0.00 on every toggle.
                    const optPriceEl = (input.closest('.form-check') || input.parentElement).querySelector('[data-price]')
                    if (optPriceEl) btnAtcPrice.textContent = optPriceEl.textContent.trim()
                }
            }
        })
    })

    const deliverEverySelect = wrapper.querySelector('#purchase-delivery-options-select')

    if (deliverEverySelect) {
        deliverEverySelect.addEventListener('change', () => {
            if (deliverEverySelect.closest('#product-template')) {
                const url = new URL(window.location)
                url.searchParams.set('selling_plan', wrapper.querySelector('[name="selling_plan"]').value)
                window.history.replaceState({}, '', url)
            }
        })
    }

    if (wrapper.dataset.subSelectedFirst === 'true') {
        wrapper.querySelector('input#purchase-options-subscription').checked = true
        wrapper.querySelector('input#purchase-options-subscription').dispatchEvent(new Event('change'))
    }
}
initPurchaseOptions()

// Product Item - Handle variant change
window.onChangeProductItemVariant = (select, event) => {
    if (select.options[select.selectedIndex].dataset.variantImage?.length) {
        select.closest('.product-item').querySelector('.product-item-img').src =
            select.options[select.selectedIndex].dataset.variantImage
    }

    const compareAtPrice = select.options[select.selectedIndex].dataset.compareAtPrice
    const price = select.options[select.selectedIndex].dataset.price

    if (compareAtPrice.length) {
        select.closest('.product-item').querySelector('.product-item-price').innerHTML = `
            <span class="product-item-price-compare text-muted me-1">
                <span class="visually-hidden">
                    ${window.theme.product.priceFrom} &nbsp;
                </span>
                <s>
                    ${Shopify.formatMoney(compareAtPrice)}
                </s>
            </span>
            <span class="product-item-price-final">
                <span class="visually-hidden">
                    ${window.theme.product.priceSale} &nbsp;
                </span>
                ${Shopify.formatMoney(price)}
            </span>
        `
    } else {
        select.closest('.product-item').querySelector('.product-item-price').innerHTML = `
            <span class="product-item-price-final">
                ${Shopify.formatMoney(price)}
            </span>
        `
    }
}

/*
    'Buy it now' button
*/
window.onClickBuyBtn = (btn) => {
    btn.innerHTML = `
        <div class="spinner-border spinner-border-sm" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
    `
    const form = btn.closest('form')
    const variantId = form.querySelector('[name="id"]').value
    const qty = Number(form.querySelector('input[name="quantity"]')?.value || 1)
    window.location.href = `/cart/${variantId}:${qty}`
}

/*
    Product Gallery
*/
const initProductGallery = () => {
    document.querySelectorAll('.product-gallery:not(.init)').forEach(gallery => {
        gallery.classList.add('init')

        const main = new Splide(gallery.querySelector('.main-splide'), {
            start: Number(gallery.dataset.start),
            rewind: true,
            arrows: false,
            rewindByDrag: true,
            pagination: false,
            noDrag: 'model-viewer',
            mediaQuery: 'min',
            breakpoints: {
                0: { padding: { right: gallery.dataset.showThumbsMobile === 'false' ? '4rem' : 0 } },
                992: { padding: 0 }
            },
            direction: document.documentElement.getAttribute('dir')
        })

        const totalSlides = gallery.querySelectorAll('.main-splide .splide__slide').length

        const thumbs = new Splide(gallery.querySelector('.thumbs-splide'), {
            start: Number(gallery.dataset.start),
            arrows: Number(gallery.dataset.mediaCount > 3),
            gap: '.5rem',
            rewind: true,
            rewindByDrag: true,
            pagination: false,
            isNavigation: true,
            focus: totalSlides > 5 ? 'center' : 0,
            mediaQuery: 'min',
            breakpoints: {
                0: { perPage: 5 },
                576: { perPage: 5 },
                768: { perPage: 5 },
                992: { perPage: 5 },
                1200: { perPage: 5 },
                1400: { perPage: 5 }
            },
            direction: document.documentElement.getAttribute('dir')
        })

        main.on('move', (newIndex, prevIndex) => {
            const iframe = gallery.querySelector(`.splide__slide:nth-child(${prevIndex + 1}) iframe`)
            const video = gallery.querySelector(`.splide__slide:nth-child(${prevIndex + 1}) video`)
            if (iframe) {
                iframe.src = iframe.src
            }
            if (video) {
                video.pause()
            }
        })

        window.addEventListener('variantChange.ecomify.product', (event) => {
            if (main.root.closest('.product-gallery-variant-based')) return

            const selectedVariant = event.detail

            if (selectedVariant.featured_media) {
                main.go(selectedVariant.featured_media.position - 1)
            }
        }, false)

        main.sync(thumbs)
        main.mount()
        thumbs.mount()

        if (window.matchMedia('(min-width: 992px)').matches) {
            const navbarHeight = document.querySelector('[id*="__navbar"].sticky-top')?.clientHeight || 0
            const announcementBarHeight = document.querySelector('[id*="__announcement-bar"].sticky-top')?.clientHeight || 0

            const galleryWrapper = gallery.closest('.product-gallery-wrapper')
            if(!galleryWrapper) return
            galleryWrapper.style.position = 'sticky'
            galleryWrapper.style.top = `${navbarHeight + announcementBarHeight + 20}px`
            galleryWrapper.style.zIndex = '1'
        }

        if (window.GLightbox) {
            const lightbox = GLightbox({
                selector: `[data-gallery="product-gallery-${gallery.dataset.productId}"]`,
                videosWidth: '1290px',
                loop: true
            })
            lightbox.on('open', () => {
                gallery.querySelectorAll('video').forEach(video => {
                    video.pause()
                })
            })
            lightbox.on('slide_changed', ({ prev, current }) => {
                main.go(current.index)
            })
        }

        const modelViewer = gallery.querySelector('model-viewer')

        if (modelViewer) {
            const increaseBtn = gallery.querySelector('[data-model-3d-increase-zoom]')
            const decreaseBtn = gallery.querySelector('[data-model-3d-decrease-zoom]')
            const fullscreenBtn = gallery.querySelector('[data-model-3d-fullscreen]')

            increaseBtn.addEventListener('click', () => {
                modelViewer.zoom(1)
            })

            decreaseBtn.addEventListener('click', () => {
                modelViewer.zoom(-1)
            })

            fullscreenBtn.addEventListener('click', () => {
                if (document.fullscreenElement) {
                    document.exitFullscreen()
                    fullscreenBtn.querySelector('.bi-fullscreen').removeAttribute('hidden')
                    fullscreenBtn.querySelector('.bi-fullscreen-exit').setAttribute('hidden', 'hidden')
                } else {
                    modelViewer.closest('.product-gallery-model-3d').requestFullscreen()
                    fullscreenBtn.querySelector('.bi-fullscreen').setAttribute('hidden', 'hidden')
                    fullscreenBtn.querySelector('.bi-fullscreen-exit').removeAttribute('hidden')
                }
            })
        }
    })
}
initProductGallery()

document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('.product-gallery')) {
        initProductGallery()
    }
})

window.addEventListener('updated.ecomify.collection', initProductGallery)

/*
    Inventory Bar
*/
const initInventoryBar = () => {
    const wrapper = document.querySelector('#inventory-bar')

    if (!wrapper) return

    const progressBar = wrapper.querySelector('.progress-bar')

    setTimeout(() => {
        progressBar.style.width = progressBar.dataset.width
    }, 250)
}
initInventoryBar()

document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('#inventory-bar')) {
        initInventoryBar()
    }
})

/*
    Upsell modal
*/
const initUpsellModal = () => {
    const modal = document.querySelector('#product-upsell-modal')
    if (!modal) return

    const form = document.querySelector('#product-content form[onsubmit*="onSubmitAtcForm"]')
    if (!form) return

    form.setAttribute('data-ecomify-upsell-modal', 'true')

    form.addEventListener('submit', (e) => {
        const modal = bootstrap.Modal.getOrCreateInstance('#product-upsell-modal')
        modal.show()
    })

    document.querySelectorAll('#product-upsell-modal .btn-atc').forEach(btn => {
        btn.addEventListener('click', () => {
            const footerBtn = document.querySelector('#product-upsell-modal .modal-footer .btn')
            footerBtn.classList.remove(footerBtn.dataset.defaultClass)
            footerBtn.classList.add(footerBtn.dataset.activeClass)
        })
    })

    modal.addEventListener('hidden.bs.modal', () => {
        if (document.querySelector('#offcanvas-cart')) {
            const offcanvasCart = bootstrap.Offcanvas.getOrCreateInstance('#offcanvas-cart')
            offcanvasCart.show()
        }
    })
}
initUpsellModal()

document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('#product-upsell-modal')) {
        initUpsellModal()
    }
})

/*
    Bought Together
*/
const initBoughtTogether = () => {
    if (!document.querySelector('#product-bought-together')) return

    const setTotalPrice = () => {
        let totalCompareAtPrice = 0
        let totalPrice = 0

        document.querySelectorAll('#product-bought-together .product-item-price').forEach(elem => {
            if (elem.closest('.product-item').querySelector('.bought-together-check:checked')) {
                totalCompareAtPrice += elem.dataset.compareAtPrice.length ? Number(elem.dataset.compareAtPrice) : Number(elem.dataset.price)
                totalPrice += Number(elem.dataset.price)
            }
        })

        const totalElem = document.querySelector('#bought-together-total')

        if (totalCompareAtPrice > totalPrice) {
            totalElem.innerHTML = `
                ${totalElem.dataset.textTotalPrice}: 
                <span class="bought-together-total-compare-price text-muted me-2">
                    <span class="visually-hidden">
                        ${window.theme.product.priceFrom} &nbsp;
                    </span>
                    <s>
                        ${Shopify.formatMoney(totalCompareAtPrice)}
                    </s>
                </span>
                <span class="bought-together-total-price text-success">
                    <span class="visually-hidden">
                        ${window.theme.product.priceSale} &nbsp;
                    </span>
                    ${Shopify.formatMoney(totalPrice)}
                </span>
            `
        } else {
            totalElem.innerHTML = `
                ${totalElem.dataset.textTotalPrice}: 
                <span class="bought-together-total-price">
                    ${Shopify.formatMoney(totalPrice)}
                </span>
            `
        }
    }

    document.querySelectorAll('.bought-together-check').forEach((checkbox) => {
        checkbox.addEventListener('change', () => {
            if (checkbox.checked) {
                checkbox.closest('.product-item').style.opacity = '1'
            } else {
                checkbox.closest('.product-item').style.opacity = '.5'
            }

            if (document.querySelector('.bought-together-check:checked')) {
                document.querySelector('#bought-together-atc').disabled = false
            } else {
                document.querySelector('#bought-together-atc').disabled = true
            }

            setTotalPrice()
        })
    })

    document.querySelectorAll('#product-bought-together select[name="id"]').forEach(select => {
        select.addEventListener('change', () => {
            const compareAtPrice = select.options[select.selectedIndex].dataset.compareAtPrice
            const price = select.options[select.selectedIndex].dataset.price

            select.closest('.product-item').querySelector('.product-item-price')
                .setAttribute('data-compare-at-price', compareAtPrice)
            select.closest('.product-item').querySelector('.product-item-price')
                .setAttribute('data-price', price)

            setTotalPrice()
        })
    })

    document.querySelector('#bought-together-atc').addEventListener('click', async (e) => {
        const btn = e.target

        btn.style.width = btn.clientWidth + 4 + 'px'
        btn.innerHTML = `
            <div class="spinner-border spinner-border-sm" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        `

        const items = []

        document.querySelectorAll('#product-bought-together .product-item').forEach(elem => {
            if (elem.querySelector('.bought-together-check:checked')) {
                items.push({
                    id: Number(elem.querySelector('[name="id"]').value),
                    quantity: 1
                })
            }
        })

        const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items })
        })

        btn.innerHTML = window.theme.product.addedToCart

        setTimeout(() => {
            btn.innerHTML = btn.dataset.textAddToCart
        }, 2000)

        window.refreshCartContents(response)

        bootstrap.Offcanvas.getOrCreateInstance('#offcanvas-cart').show()
    })
}
initBoughtTogether()

/*
    Qty Breaks
*/
const initQtyBreaks = () => {
    document.querySelectorAll('[name="product-qty-breaks"]').forEach(radio => {
        radio.addEventListener('change', () => {

            const qty = Number(radio.dataset.qty)

            const qtyField = radio.closest('form').querySelector('[name="quantity"]')

            const form = radio.closest('form')
            const btn = form.querySelector('button[name="add"]')

            if (!qtyField) return

            qtyField.value = qty
            qtyField.dispatchEvent(new Event('change'))

            setTimeout(() => {
                btn.classList.add('animate__animated', 'animate__shakeX')
            }, 250)

            setTimeout(() => {
                btn.classList.remove('animate__animated', 'animate__shakeX')
            }, 1500)

            if (form.querySelector('.btn-atc-price')) {
                form.querySelector('.btn-atc-price').textContent = radio.dataset.totalPrice
            }
        })
    })
}
initQtyBreaks()

/*
    Prev/Next Products (will show only if the product is within a collection)
*/
const initPrevNextButtons = async () => {
    const wrapper = document.querySelector('#product-prev-next')

    if (!wrapper) return

    if (!window.matchMedia('(max-width: 1599px)').matches) return

    await new Promise(resolve => setTimeout(resolve, 1000))

    const threshold = document.querySelector('.product-content form[action*="/cart/add"]').getBoundingClientRect().bottom + window.scrollY

    window.addEventListener('scroll', () => {
        if (window.scrollY > threshold) {
            wrapper.classList.add('show')
        } else {
            wrapper.classList.remove('show')
        }
    })
}
initPrevNextButtons()

/*
    Personalization (Custom properties)
*/
const initPersonalization = () => {
    document.querySelectorAll('.product-personalization-field[data-type="checkbox"]').forEach(elem => {
        let values = []

        elem.querySelectorAll('input').forEach(checkbox => {
            checkbox.addEventListener('change', () => {
                if (checkbox.checked) {
                    values.push(checkbox.value)
                } else {
                    values = values.filter((item) => item !== checkbox.value)
                }

                elem.querySelector('[type="hidden"]').value = values.join(', ')
            })
        })
    })
}
initPersonalization()

/*
    Limited Offer
*/
const initLimitedOffer = () => {
    const block = document.querySelector('#product-limited-offer')

    if (!block) return

    const icon = block.querySelector('.block-icon')

    setInterval(() => {
        icon.classList.add('animate__animated', 'animate__tada')

        setTimeout(() => {
            icon.classList.remove('animate__animated', 'animate__tada')
        }, 1500)
    }, 3000)
}
initLimitedOffer()

/*
    Sync quantity between the product form, Sticky ATC, etc
*/
const initSyncQuantity = () => {
    const productFormQtyInput = document.querySelector('.product-buy-buttons input[name="quantity"]')

    if (!productFormQtyInput) return

    productFormQtyInput.addEventListener('change', () => {
        const stickyAtcQtyInput = document.querySelector('#sticky-atc input[name="quantity"]')

        if (!stickyAtcQtyInput) return

        stickyAtcQtyInput.value = productFormQtyInput.value
    })
}
initSyncQuantity()

/* ── PER-UNIT PRICE: update per-stick price in main price block ─────────────
   Shows current per-stick price only (e.g. $3.60/stick).
   Strikethrough + save % are handled by the main price row above.
   Falls back to creating the element if innerHTML wipe removed it.
   ─────────────────────────────────────────────────────────────────────────── */
function _updatePerUnitPrice(productContent, selectedVariant, allVariants) {
  // Find or create the per-unit span — may have been wiped by innerHTML update
  let perUnitEl = productContent && productContent.querySelector('.product-price-per-unit');
  if (!perUnitEl) {
    const priceRow = productContent && (
      productContent.querySelector('.product-price-row') ||
      productContent.querySelector('.product-price')
    );
    if (!priceRow) return;
    perUnitEl = document.createElement('span');
    perUnitEl.className = 'product-price-per-unit';
    // Insert before any badge so order is: strikethrough | price | per-stick | save badge
    const badge = priceRow.querySelector('.price-badge-sale, .price-badge-sold-out');
    badge ? priceRow.insertBefore(perUnitEl, badge) : priceRow.appendChild(perUnitEl);
  }

  const packMatch = selectedVariant.title.match(/(\d+)\s*Pack/i);
  const packCount = packMatch ? parseInt(packMatch[1]) : 0;
  if (!packCount) { perUnitEl.innerHTML = ''; return; }

  const currentPerStick = selectedVariant.price / packCount; // cents
  const fmt = (cents) => '$' + (cents / 100).toFixed(2);
  perUnitEl.innerHTML = `<span class="per-unit-current">${fmt(currentPerStick)}/stick</span>`;
}

// Run on page load for the initially selected variant
(function _initPerUnitPrice() {
  const run = () => {
    const allVariants = window.productVariants ||
      (typeof product !== 'undefined' ? product.variants : []) ||
      (window.__productJSON ? window.__productJSON.variants : []);
    if (!allVariants || !allVariants.length) return;

    const checkedInput = document.querySelector(
      '.product-variant-selector input[name="id"], .shopify-product-form input[name="id"]'
    );
    const variantId = checkedInput
      ? parseInt(checkedInput.value)
      : (new URLSearchParams(window.location.search).get('variant') | 0);

    const selected = allVariants.find(v => v.id === variantId) || allVariants[0];
    if (!selected) return;

    const productContent = document.querySelector('.product-content');
    if (productContent) _updatePerUnitPrice(productContent, selected, allVariants);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(run, 200));
  } else {
    setTimeout(run, 200);
  }
})();
